import React from 'react';
import s from './s.module.less';
import { TITLE } from './constants';
import Frame1 from './imgs/frame1.png';
import FrequencyQuestionSecion from 'components/FrequencyQuestionSecion';
import { TFaqInfo } from 'types/operation';

//https://www.notion.so/klarityhealth/Klarity-E-Prescriptions-FAQ-29518650acdf80bdb42aeb850c7c4263?source=copy_link

// - Unlimited electronic prescriptions to over 95% of US pharmacies
// - EPCS certification for controlled substances (Schedule II-V)
// - PDMP (Prescription Drug Monitoring Program) integration
// - Real-time formulary checking and cost transparency
// - Drug interaction and allergy alerts
// - Prescription status tracking (sent, filled, denied)
// - Free software updates and security patches
// - Email and chat support during business hours
export const FAQData: TFaqInfo[] = [
    {
        id: 1,
        pageId: 1,
        seq: 1,
        question: "1. What's included in the $36/month subscription?",
        answer: `
    <b>Answer:</b> Your Klarity E-Prescriptions subscription includes:
        <ul>
            <li>
                Unlimited electronic prescriptions to over 95% of US pharmacies
            </li>
            <li>
                EPCS certification for controlled substances (Schedule II-V)
            </li>
            <li>
                PDMP (Prescription Drug Monitoring Program) integration
            </li>
            <li>
                Real-time formulary checking and cost transparency
            </li>
            <li>
                Drug interaction and allergy alerts
            </li>
            <li>
                Prescription status tracking (sent, filled, denied)
            </li>
            <li>
                Free software updates and security patches
            </li>
            <li>
                Email and chat support during business hours
            </li>
        </ul>
        There are no per-prescription fees, setup costs, or hidden charges. You can cancel anytime without penalty.
    `,
    },
    {
        id: 2,
        pageId: 2,
        seq: 2,
        question: '2. How does Klarity eRX (powered by ScriptSure) compare to using ScriptSure directly?',
        answer: `
    <b>Answer:</b> Klarity E-Prescriptions is powered by ScriptSure's certified infrastructure, which means you get the same security, pharmacy network, and EPCS compliance. The key differences:
        <ul>
            <li>
                <b>Price:</b> Klarity costs $36/month vs. ScriptSure's $45/month
            </li>
            <li>
               <b>Integration:</b> Klarity is built specifically to integrate seamlessly with Klarity's telehealth and EHR platform
            </li>
            <li>
                <b>User Experience:</b> Simplified interface designed for mental health and primary care workflows
            </li>
            <li>
               <b>Single Dashboard:</b> Manage appointments, notes, and prescriptions in one place
            </li>
        </ul>
        You'll create a ScriptSure account during setup (for regulatory compliance), but you'll work exclusively in the Klarity interface.
    `,
    },
    {
        id: 3,
        pageId: 3,
        seq: 3,
        question: 'Is Klarity E-Prescriptions EPCS-certified for controlled substances?',
        answer: `
    <b>Answer:</b> Yes. Klarity is fully EPCS (Electronic Prescribing for Controlled Substances) compliant and meets DEA requirements for prescribing Schedule II-V controlled substances electronically.
        <div><b>You'll need to complete:</b></div>    
        <ul>
            <li>
                Identity proofing (one-time verification with ID documents)
            </li>
            <li>
               Two-factor authentication setup (SMS code or authenticator app)
            </li>
            <li>
                DEA number verification
            </li>
        </ul>
         <div><b>State-specific features:</b></div>    
        <ul>
            <li>
                Automatic PDMP checking where required by state law
            </li>
            <li>
               State-specific prescription limits and restrictions
            </li>
            <li>
               Compliance with state-mandated prescription monitoring programs
            </li>
        </ul>
    `,
    },
    {
        id: 4,
        pageId: 4,
        seq: 4,
        question: 'Is Klarity E-Prescriptions EPCS-certified for controlled substances?',
        answer: `
    <b>Answer:</b> Setup takes approximately 10 minutes and follows these steps:
        <div><b>Step 1: Create ScriptSure Account</b></div>    
        <ul>
            <li>
               Provide your NPI number
            </li>
            <li>
               Enter practice phone number and ZIP code
            </li>
            <li>
               Verify professional credentials
            </li>
            <li>
               If you practice in multiple locations across different states, you’ll need to enter practice addresses for all of the locations
            </li>
        </ul>
         <div><b>Step 2: Identity Verification (for EPCS)</b></div>    
        <ul>
            <li>
                Upload government-issued ID
            </li>
            <li>
               Answer knowledge-based authentication questions
            </li>
            <li>
               DEA number verification (if prescribing controlled substances)
            </li>
            <li>
               Choose SMS or authenticator app
            </li>
            <li>
               Verify your device
            </li>
        </ul>
        You can start prescribing non-controlled medications immediately after Step 1. EPCS access is activated within 24-48 hours after identity verification is complete.
    `,
    },
    {
        id: 5,
        pageId: 5,
        seq: 5,
        question: '5. Which pharmacies can I send prescriptions to?',
        answer: `
    <b>Answer:</b> Klarity connects to over 95% of US pharmacies, including:
        <div><b>Major chains:</b></div>    
        <ul>
            <li>
              CVS, Walgreens, Walmart, Kroger, Rite Aid
            </li>
            <li>
               Costco, Sam's Club, Target
            </li>
        </ul>
         <div><b>Regional and independent pharmacies:</b></div>    
        <ul>
            <li>
                Most local independent pharmacies
            </li>
            <li>
               Grocery store pharmacies (Safeway, etc.)
            </li>
        </ul>
        <div><b>Specialty options:</b></div>    
        <ul>
            <li>
                Amazon pharmacy 
            </li>
            <li>
               Mail-order pharmacies 
            </li>
            <li>
               Specialty pharmacies for complex medications
            </li>
            <li>
               Compound pharmacies
               <ul>
                <li>If you have access to your own Compound pharmacies, you could add them into the Klarity ePrescription software </li>
               </ul>
            </li>
        </ul>
        <div><b>Not currently supported:</b></div>    
        <ul>
            <li>
                Some very small independent pharmacies without electronic systems
            </li>
            <li>
               International pharmacies
            </li>
        </ul>
        If a patient's preferred pharmacy isn't in the network, you'll be notified during prescription entry and can help them choose an alternative nearby.
    `,
    },
    {
        id: 6,
        pageId: 6,
        seq: 6,
        question: '6. Can my staff send prescriptions on my behalf?',
        answer: `
    <b>Answer:</b> Yes, with proper delegation:
        <div><b>For non-controlled medications:</b></div>    
        <ul>
            <li>
              Administrative staff can enter prescription details
            </li>
            <li>
              You review and electronically sign/approve
            </li>
            <li>
              Your NPI and signature appear on the prescription
            </li>
            <li>
              Full audit trail of who entered vs. who approved
            </li>
        </ul>
         <div><b>For controlled substances (EPCS):</b></div>    
        <ul>
            <li>
                You must personally log in with your two-factor authentication
            </li>
            <li>
               Cannot be delegated due to DEA requirements
            </li>
            <li>Staff can prepare the prescription, but you must complete final authentication</li>
        </ul>
        <div><b>Permission levels you can set:</b></div>    
        <ul>
            <li>
                <b>View Only:</b> See prescriptions but cannot create or modify
            </li>
           <li>
                <b>Enter:</b> Draft prescriptions for your review
            </li>
            <li>
                <b>Send (Non-Controlled):</b> Send non-controlled prescriptions without review
            </li>
            <li>
                <b>Admin:</b> Manage practice settings and user permissions
            </li>
        </ul>
        There’s no cost of adding up to 5 assistant seats in your practice. 
    `,
    },
    {
        id: 7,
        pageId: 7,
        seq: 7,
        question: '7. How do I gain access to the instruction to the e-RX?',
        answer: `
    <b>Answer:</b> The e-RX software is very intuitive. For common questions, please visit ScriptSure’s user FAQs: <a href="https://scriptsure.freshdesk.com/support/solutions/69000436814" target="_blank">https://scriptsure.freshdesk.com/support/solutions/69000436814</a>  `,
    },
];
const FAQ = () => {
    return (
        <div className={s.wrap}>
            <h2 className={s.title}>Frequently asked questions</h2>
            <FrequencyQuestionSecion
                openAll={false}
                data={FAQData}
                largerFontsize
            />
        </div>
    );
};

export default FAQ;
